import { useColorScheme } from '@bn-apps/dim-ui-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  chart: string;
  className?: string;
}

const MermaidDiagram = ({ chart, className }: MermaidDiagramProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const isDark = useColorScheme() === 'dark';

  const renderDiagram = useCallback(
    async (dark: boolean) => {
      if (!containerRef.current) return;

      mermaid.initialize({
        startOnLoad: false,
        theme: dark ? 'dark' : 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
        },
      });

      try {
        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, chart);
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    },
    [chart]
  );

  // Render when chart or theme changes
  useEffect(() => {
    renderDiagram(isDark);
  }, [chart, isDark, renderDiagram]);

  if (error) {
    return <div role="alert">Diagram rendering error: {error}</div>;
  }

  return (
    <div ref={containerRef} className={className} role="img" aria-label="Data lineage diagram" />
  );
};

export default MermaidDiagram;
